import {CommonModule, DatePipe} from '@angular/common';
import {HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi} from '@angular/common/http';
import {ErrorHandler, NgModule} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {BrowserModule} from '@angular/platform-browser';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {RouteReuseStrategy} from '@angular/router';
import * as Sentry from '@sentry/angular';
import {NgxSkideoCreateModule, SkideoCreateService} from '@vasio-nl/skideo-create-lib';
import {ModalModule} from 'ngx-bootstrap/modal';
import {ClipboardModule} from 'ngx-clipboard';
import {NgxMaterialIntlTelInputComponent} from 'ngx-material-intl-tel-input';
import {provideLottieOptions} from 'ngx-lottie';
import {ToastrModule} from 'ngx-toastr';
import {LottiePlayer} from 'lottie-web';
import player from 'lottie-web';

import {CustomRouteReuseStrategy} from './app-route-reuse-strategy';
import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {GenericErrorComponent} from './shared/components/generic-error/generic-error.component';
import {RegisterMenuToggleDirective} from './shared/directives/register-menu-toggle.directive';
import {GraphQLModule} from './shared/graphql.module';
import {HttpConfigInterceptor} from './shared/httpconfig.interceptors';
import {TemplateDeleteComponent} from './shared/modals/template-delete/template-delete.component';
import {TemplateSaveComponent} from './shared/modals/template-save/template-save.component';
import {TemplateTitleComponent} from './shared/modals/template-title/template-title.component';
import {ReportDataPipe} from './shared/pipes/report-data';
import {ShortNumberPipe} from './shared/pipes/short-number';
import {TemplateService} from './shared/services/template.service';
import {BuilderFormComponent} from './views/builder/builder-form/builder-form.component';
import {BuilderOverviewComponent} from './views/builder/builder-overview/builder-overview.component';
import {BuilderComponent} from './views/builder/builder.component';
import {ContentViewComponent} from './views/content/content-view/content-view.component';
import {ContentComponent} from './views/content/content.component';
import {CreditAddonsComponent} from './views/dashboard/credit-addons/credit-addons.component';
import {CreditUsageComponent} from './views/dashboard/credit-usage/credit-usage.component';
import {DashboardComponent} from './views/dashboard/dashboard.component';
import {ErrorPageComponent} from './views/error-page/error-page.component';
import {HeaderComponent} from './views/header/header.component';
import {HomeComponent} from './views/home/home.component';
import {LandingComponent} from './views/landing/landing.component';
import {ReportsComponent} from './views/reports/reports.component';
import {SideNavComponent} from './views/side-nav/side-nav.component';
import {TemplateDetailsComponent} from './views/templates/template-details/template-details.component';
import {TemplateOverviewComponent} from './views/templates/template-overview/template-overview.component';
import {TemplatesComponent} from './views/templates/templates.component';
import {CreateUserComponent} from './views/users/create-user/create-user.component';
import {UserEditComponent} from './views/users/user-edit/user-edit.component';
import {UserComponent} from './views/users/user/user.component';
import {UsersComponent} from './views/users/users.component';

// Required by AOT compiler, see docs: https://www.npmjs.com/package/ngx-lottie#usage
export function playerFactory(): LottiePlayer {
  return player;
}

@NgModule({
  declarations: [
    AppComponent,
    SideNavComponent,
    DashboardComponent,
    UsersComponent,
    ContentComponent,
    UserComponent,
    HeaderComponent,
    HomeComponent,
    ErrorPageComponent,
    ContentViewComponent,
    ShortNumberPipe,
    CreateUserComponent,
    UserEditComponent,
    GenericErrorComponent,
    ReportsComponent,
    LandingComponent,
    ReportDataPipe,
    BuilderComponent,
    BuilderFormComponent,
    BuilderOverviewComponent,
    TemplatesComponent,
    TemplateOverviewComponent,
    TemplateDetailsComponent,
    TemplateDeleteComponent,
    TemplateSaveComponent,
    TemplateTitleComponent,
    RegisterMenuToggleDirective,
    CreditUsageComponent,
    CreditAddonsComponent,
  ],
  bootstrap: [AppComponent],
  imports: [
    AppRoutingModule,
    CommonModule,
    BrowserModule,
    BrowserAnimationsModule,
    GraphQLModule,
    FormsModule,
    ReactiveFormsModule,
    NgxMaterialIntlTelInputComponent,
    ToastrModule.forRoot({
      preventDuplicates: true,
      maxOpened: 2,
    }),
    ModalModule,
    ClipboardModule,
    NgxSkideoCreateModule,
    MatProgressSpinnerModule,
  ],
  providers: [
    ...provideLottieOptions({player: playerFactory}),
    {provide: HTTP_INTERCEPTORS, useClass: HttpConfigInterceptor, multi: true},
    SkideoCreateService,
    TemplateService,
    {provide: RouteReuseStrategy, useClass: CustomRouteReuseStrategy},
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    DatePipe,
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}
